/**
 * CAD Document Model
 * Represents a CAD document with features, sketches, parts, and assemblies
 * Follows CoandaView's existing Vanilla JS class patterns
 */

/**
 * CAD Document - Contains multiple parts that can be assembled (like Onshape)
 */
class CADDocument {
  constructor(data = {}) {
    this.id = data.id || Utils.generateId();
    this.name = data.name || 'Untitled Document';
    this.description = data.description || '';
    this.version = data.document_version || '1.0.0';
    this.createdAt = data.created_at ? new Date(data.created_at) : new Date();
    this.updatedAt = data.updated_at ? new Date(data.updated_at) : new Date();

    // Feature tree (ordered list of operations)
    this.features = data.features || [];

    // 2D Sketches (unlimited, on any plane/face)
    this.sketches = data.sketches || [];

    // 3D Parts (multiple parts per document, like Onshape)
    this.parts = data.parts || [];

    // Assemblies (combine parts with mates)
    this.assemblies = data.assemblies || [];

    // Document metadata
    this.metadata = data.metadata || {};
    this.settings = data.settings || {};

    // Storage paths
    this.geometryPath = data.geometry_path || null;
    this.thumbnailPath = data.thumbnail_path || null;

    // Sharing
    this.isShared = data.is_shared || false;
    this.accessType = data.access_type || 'private';

    // Tags for categorization
    this.tags = data.tags || [];

    // Unsaved changes flag
    this.hasUnsavedChanges = false;
  }

  /**
   * Add a feature to the document
   */
  addFeature(feature) {
    this.features.push(feature);
    this.hasUnsavedChanges = true;
    this.updatedAt = new Date();
  }

  /**
   * Update a feature by ID
   */
  updateFeature(featureId, updates) {
    const feature = this.features.find(f => f.id === featureId);
    if (feature) {
      Object.assign(feature, updates);
      this.hasUnsavedChanges = true;
      this.updatedAt = new Date();
      return true;
    }
    return false;
  }

  /**
   * Delete a feature by ID
   */
  deleteFeature(featureId) {
    const index = this.features.findIndex(f => f.id === featureId);
    if (index !== -1) {
      this.features.splice(index, 1);
      this.hasUnsavedChanges = true;
      this.updatedAt = new Date();
      return true;
    }
    return false;
  }

  /**
   * Get feature by ID
   */
  getFeature(featureId) {
    return this.features.find(f => f.id === featureId);
  }

  /**
   * Reorder feature in tree
   */
  reorderFeature(featureId, newIndex) {
    const currentIndex = this.features.findIndex(f => f.id === featureId);
    if (currentIndex === -1) return false;

    const [feature] = this.features.splice(currentIndex, 1);
    this.features.splice(newIndex, 0, feature);
    this.hasUnsavedChanges = true;
    this.updatedAt = new Date();
    return true;
  }

  /**
   * Serialize to JSON for saving
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      document_version: this.version,
      features: this.features,
      sketches: this.sketches,
      parts: this.parts,
      assemblies: this.assemblies,
      metadata: this.metadata,
      settings: this.settings,
      geometry_path: this.geometryPath,
      thumbnail_path: this.thumbnailPath,
      is_shared: this.isShared,
      access_type: this.accessType,
      tags: this.tags
    };
  }

  /**
   * Create document from API response
   */
  static fromJSON(data) {
    return new CADDocument(data);
  }
}

/**
 * Feature - A single operation in the parametric history
 */
class Feature {
  constructor(id, type, parameters = {}) {
    this.id = id || Utils.generateId();
    this.type = type;           // 'sketch', 'extrude', 'revolve', etc.
    this.name = parameters.name || this.getDefaultName(type);
    this.parameters = parameters;
    this.dependencies = [];     // IDs of parent features
    this.status = 'valid';      // 'valid', 'invalid', 'suppressed'
    this.geometry = null;       // Cached B-rep result
    this.visible = true;
  }

  getDefaultName(type) {
    const names = {
      sketch: 'Sketch',
      extrude: 'Extrude',
      revolve: 'Revolve',
      sweep: 'Sweep',
      loft: 'Loft',
      fillet: 'Fillet',
      chamfer: 'Chamfer',
      shell: 'Shell',
      pattern: 'Pattern',
      boolean: 'Boolean',
      mirror: 'Mirror'
    };
    return names[type] || 'Feature';
  }

  /**
   * Suppress/unsuppress feature
   */
  setSuppressed(suppressed) {
    this.status = suppressed ? 'suppressed' : 'valid';
  }

  /**
   * Check if feature is suppressed
   */
  isSuppressed() {
    return this.status === 'suppressed';
  }
}

/**
 * Sketch - 2D drawing on a plane (unlimited sketches per document)
 */
class Sketch {
  constructor(id, name, plane) {
    this.id = id || Utils.generateId();
    this.name = name || 'Sketch';
    this.plane = plane;         // SketchPlane object
    this.entities = [];         // Lines, arcs, circles, splines
    this.constraints = [];      // Geometric constraints
    this.dimensions = [];       // Dimensional constraints
    this.solved = false;        // Constraint solver status
    this.visible = true;
  }

  /**
   * Add entity to sketch
   */
  addEntity(entity) {
    this.entities.push(entity);
    this.solved = false;  // Need to re-solve constraints
  }

  /**
   * Add constraint to sketch
   */
  addConstraint(constraint) {
    this.constraints.push(constraint);
    this.solved = false;
  }

  /**
   * Get all entities of a specific type
   */
  getEntitiesByType(type) {
    return this.entities.filter(e => e.type === type);
  }
}

/**
 * SketchPlane - Defines where a sketch lives in 3D space
 */
class SketchPlane {
  constructor(type, origin, normal, xDirection) {
    this.type = type;           // 'xy', 'xz', 'yz', 'face', 'custom'
    this.origin = origin;       // {x, y, z}
    this.normal = normal;       // {x, y, z}
    this.xDirection = xDirection; // {x, y, z}
  }

  // Convenience factory methods for standard planes
  static XY() {
    return new SketchPlane(
      'xy',
      { x: 0, y: 0, z: 0 },
      { x: 0, y: 0, z: 1 },
      { x: 1, y: 0, z: 0 }
    );
  }

  static XZ() {
    return new SketchPlane(
      'xz',
      { x: 0, y: 0, z: 0 },
      { x: 0, y: 1, z: 0 },
      { x: 1, y: 0, z: 0 }
    );
  }

  static YZ() {
    return new SketchPlane(
      'yz',
      { x: 0, y: 0, z: 0 },
      { x: 1, y: 0, z: 0 },
      { x: 0, y: 1, z: 0 }
    );
  }

  /**
   * Create plane from a 3D face
   */
  static fromFace(face, point) {
    // Extract normal and origin from face geometry
    // For now, return XY plane as placeholder
    console.warn('SketchPlane.fromFace() not yet implemented');
    return SketchPlane.XY();
  }

  /**
   * Convert to Three.js coordinate system
   */
  toThreePlane() {
    return new THREE.Plane(
      new THREE.Vector3(this.normal.x, this.normal.y, this.normal.z),
      -new THREE.Vector3(this.origin.x, this.origin.y, this.origin.z).dot(
        new THREE.Vector3(this.normal.x, this.normal.y, this.normal.z)
      )
    );
  }
}

/**
 * Part - A 3D solid body (multiple parts per document, like Onshape)
 */
class Part {
  constructor(id, name) {
    this.id = id || Utils.generateId();
    this.name = name || 'Part';

    // B-rep representation (exact geometry)
    this.brep = null;           // Serialized B-rep data

    // Tessellated mesh for Three.js display
    this.mesh = null;           // {positions, normals, indices}

    // Bounding box for quick culling
    this.boundingBox = null;

    // Material and appearance
    this.appearance = {
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.5,
      opacity: 1.0
    };

    // Computed properties (from geometry kernel)
    this.properties = {
      volume: 0,
      surfaceArea: 0,
      centerOfMass: { x: 0, y: 0, z: 0 },
      momentOfInertia: null
    };

    // Visibility
    this.visible = true;
  }

  /**
   * Create Three.js mesh from tessellation data
   */
  toThreeMesh() {
    if (!this.mesh) {
      console.warn('Part has no mesh data');
      return null;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.mesh.positions, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(this.mesh.normals, 3));
    geometry.setIndex(new THREE.BufferAttribute(this.mesh.indices, 1));

    const material = new THREE.MeshStandardMaterial({
      color: this.appearance.color,
      metalness: this.appearance.metalness,
      roughness: this.appearance.roughness,
      transparent: this.appearance.opacity < 1.0,
      opacity: this.appearance.opacity
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADPart: true,
      partId: this.id,
      partName: this.name
    };

    return mesh;
  }
}

/**
 * Assembly - Combines multiple parts with mates (like Onshape)
 */
class Assembly {
  constructor(id, name) {
    this.id = id || Utils.generateId();
    this.name = name || 'Assembly';
    this.components = [];       // Part instances with transforms
    this.mates = [];            // Constraints between parts
    this.subAssemblies = [];    // Nested assemblies
    this.isSimulating = false;  // Motion simulation state
  }

  /**
   * Add component (part instance) to assembly
   */
  addComponent(partId, transform = null) {
    const component = {
      id: Utils.generateId(),
      partId: partId,
      transform: transform || {
        position: [0, 0, 0],
        rotation: [0, 0, 0, 1]  // Quaternion
      },
      visible: true,
      fixed: false  // Fixed components don't move during simulation
    };
    this.components.push(component);
    return component;
  }

  /**
   * Add mate (constraint) between components
   */
  addMate(mate) {
    this.mates.push(mate);
    return mate.id;
  }

  /**
   * Get component by ID
   */
  getComponent(componentId) {
    return this.components.find(c => c.id === componentId);
  }

  /**
   * Remove component by ID
   */
  removeComponent(componentId) {
    const index = this.components.findIndex(c => c.id === componentId);
    if (index !== -1) {
      this.components.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Remove mate by ID
   */
  removeMate(mateId) {
    const index = this.mates.findIndex(m => m.id === mateId);
    if (index !== -1) {
      this.mates.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Solve mates (apply all constraints)
   */
  solveMates() {
    // Apply each mate constraint
    for (const mate of this.mates) {
      this.applyMate(mate);
    }
  }

  /**
   * Apply a single mate constraint
   */
  applyMate(mate) {
    const comp1 = this.getComponent(mate.component1);
    const comp2 = this.getComponent(mate.component2);

    if (!comp1 || !comp2) return;
    if (comp1.fixed && comp2.fixed) return;

    switch (mate.type) {
      case 'fastened':
        this.applyFastenedMate(comp1, comp2, mate);
        break;
      case 'revolute':
        this.applyRevoluteMate(comp1, comp2, mate);
        break;
      case 'slider':
        this.applySliderMate(comp1, comp2, mate);
        break;
      case 'coincident':
        this.applyCoincidentMate(comp1, comp2, mate);
        break;
      case 'parallel':
        this.applyParallelMate(comp1, comp2, mate);
        break;
      case 'perpendicular':
        this.applyPerpendicularMate(comp1, comp2, mate);
        break;
    }
  }

  /**
   * Fastened mate - components rigidly attached
   */
  applyFastenedMate(comp1, comp2, mate) {
    if (!comp1.fixed && comp2.fixed) {
      // Comp2 is fixed, move comp1 to match
      comp1.transform.position = [...comp2.transform.position];
      comp1.transform.rotation = [...comp2.transform.rotation];
    } else if (!comp2.fixed) {
      // Move comp2 to match comp1
      comp2.transform.position = [...comp1.transform.position];
      comp2.transform.rotation = [...comp1.transform.rotation];
    }
  }

  /**
   * Revolute mate - components can rotate about axis
   */
  applyRevoluteMate(comp1, comp2, mate) {
    // Allow rotation about specified axis
    // Constrain position and other rotation axes
    if (mate.angle !== undefined) {
      // Set specific angle
      // Implementation would update rotation based on axis and angle
    }
  }

  /**
   * Slider mate - components can slide along axis
   */
  applySliderMate(comp1, comp2, mate) {
    // Allow translation along specified axis
    // Constrain rotation and other translation axes
    if (mate.distance !== undefined) {
      // Set specific distance
      // Implementation would update position based on axis and distance
    }
  }

  /**
   * Coincident mate - faces/planes aligned
   */
  applyCoincidentMate(comp1, comp2, mate) {
    // Align specified faces or planes
    // Similar to fastened but may allow rotation
  }

  /**
   * Parallel mate - faces/axes parallel
   */
  applyParallelMate(comp1, comp2, mate) {
    // Make specified axes or normals parallel
  }

  /**
   * Perpendicular mate - faces/axes perpendicular
   */
  applyPerpendicularMate(comp1, comp2, mate) {
    // Make specified axes or normals perpendicular
  }
}

/**
 * Mate - Constraint between assembly components
 */
class Mate {
  constructor(type, component1, component2, parameters = {}) {
    this.id = Utils.generateId();
    this.type = type;  // 'fastened', 'revolute', 'slider', 'coincident', etc.
    this.component1 = component1;  // Component ID
    this.component2 = component2;  // Component ID
    this.parameters = parameters;  // Type-specific parameters

    // Common parameters
    this.enabled = true;
    this.suppressed = false;

    // Type-specific defaults
    switch (type) {
      case 'revolute':
        this.axis = parameters.axis || [0, 1, 0];
        this.angle = parameters.angle || 0;
        break;
      case 'slider':
        this.axis = parameters.axis || [1, 0, 0];
        this.distance = parameters.distance || 0;
        this.minDistance = parameters.minDistance || -Infinity;
        this.maxDistance = parameters.maxDistance || Infinity;
        break;
      case 'fastened':
        this.offset = parameters.offset || [0, 0, 0];
        break;
    }
  }

  /**
   * Toggle mate enabled state
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  /**
   * Suppress mate (temporarily disable)
   */
  setSuppressed(suppressed) {
    this.suppressed = suppressed;
  }
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADDocument, Feature, Sketch, SketchPlane, Part, Assembly };
}
